import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

// Password management is handled by Apple ID / App Store sign-in, so the
// in-app "Change Password" tab was removed. Native team_managers never have
// a password (silent App Store identity); legacy email/password admins
// manage their credentials through Apple's account flow.
export default function EditProfileModal({ open, onOpenChange, user, onSuccess }) {
  const [name, setName] = useState(user?.name || '');
  const [saving, setSaving] = useState(false);

  // Reset name when user changes
  useEffect(() => {
    if (user?.name) {
      setName(user.name);
    }
  }, [user]);

  const handleProfileSubmit = async (e) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }

    setSaving(true);
    try {
      const response = await api.patch(`/users/me`, { name: name.trim() });
      toast.success('Profile updated successfully');
      onOpenChange(false);
      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-profile-modal" className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase text-2xl">Settings</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleProfileSubmit}>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="profileName" className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 block">
                Name *
              </Label>
              <Input
                id="profileName"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your name"
                required
                className="h-12"
                data-testid="edit-profile-name-input"
              />
            </div>

            <div>
              <Label className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 block">
                Email
              </Label>
              <Input
                value={user?.email || ''}
                disabled
                className="h-12 bg-slate-50 text-slate-500"
              />
              <p className="text-xs text-slate-500 mt-1">Email cannot be changed</p>
            </div>

            {/* Account ID (read-only, auto-generated) */}
            {user?.account_id && (
              <div>
                <Label className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 block">
                  Account ID
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    value={user.account_id}
                    disabled
                    className="h-12 bg-slate-50 text-slate-700 font-mono tracking-wider"
                    data-testid="account-id-field"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="h-12 px-3"
                    onClick={() => { navigator.clipboard.writeText(user.account_id); toast.success('Account ID copied!'); }}
                    data-testid="copy-account-id"
                  >
                    Copy
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-1">Reference this ID when contacting support.</p>
              </div>
            )}

            {/* Athlete ID (read-only, for players and fan seats) */}
            {user?.athlete_id && (user?.role === 'player') && (
              <div>
                <Label className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 block">
                  Athlete ID
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    value={user.athlete_id}
                    disabled
                    className="h-12 bg-slate-50 text-slate-700 font-mono tracking-wider"
                    data-testid="athlete-id-settings-field"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="h-12 px-3"
                    onClick={() => { navigator.clipboard.writeText(user.athlete_id); toast.success('Athlete ID copied!'); }}
                    data-testid="copy-athlete-id-settings"
                  >
                    Copy
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-1">Share this with a parent so they can link your account from their dashboard.</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="save-profile-button"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
